import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { ExerciseDb } from 'lib/cosmos/exercise/exerciseDb.js';
import { sortByRelevance } from 'lib/helpers/search.utils.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { LiteralUnion } from 'lib/types/lib.types.js';
import { match, P } from 'ts-pattern';
import { IButton } from '../buttons/button.models.js';
import { CoreComponent } from '../core/core.component.js';
import { ISearchInputResult } from '../form-inputs/search/search-input.component.js';
import { SelectItemField } from '../form/fields/select-item-field.component.js';
import { InfoTableItem } from '../simple-table/info-table.component.js';
import { IImage } from '../ui-models.js';

export type ExerciseListItem = {
	readonly name: string;
	readonly id: string;
	readonly isGlobal: boolean;

	readonly videoUrl: string | undefined;
	readonly equipment: LiteralUnion<ExerciseDb.Equipment> | undefined;
	readonly difficulty: ExerciseDb.DifficultyLevel | undefined;
	readonly instructions: string | undefined;
	readonly forceType: ExerciseDb.ForceType | undefined;
	readonly laterality: ExerciseDb.Laterality | undefined;
	readonly images: readonly IImage[];
	readonly targetMuscleGroup: ExerciseDb.TargetMuscleGroup | undefined;
	readonly select?: () => void;
};

export type ExtendedExerciseListItem = ExerciseListItem & {
	readonly filteredImages: readonly IImage[];
	readonly subtitle: string | undefined;
	readonly infoTableItems: readonly InfoTableItem[];
};

type ActiveFilter = {
	readonly filter: ExerciseFilter;
	readonly option: SelectItemField<undefined>;
};

type ExerciseFilter = {
	readonly applyFilter: (item: ExerciseListItem, option: SelectItemField<undefined>) => boolean;
	readonly title: string;
	readonly options: readonly SelectItemField<undefined>[];
};

@Component({
	selector: 'lib-select-exercise-list',
	templateUrl: './select-exercise-list.component.html',
	styleUrls: ['./select-exercise-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SelectExerciseListComponent extends CoreComponent {
	public readonly items = input.required<readonly ExerciseListItem[]>();
	public readonly pageSize = input<number>(20);
	public readonly selectedItemId = input<string>();
	public readonly selectExerciseButtonText = input<string>('Add to workout');
	public readonly isInDialog = input<boolean>(false);
	public readonly selectItem = output<ExerciseListItem>();

	private readonly showSearchItemsThreshold: number = 1;
	private readonly search = signal<ISearchInputResult | undefined>(undefined);
	public readonly filteredItems = computed<readonly ExerciseListItem[] | undefined>(() => {
		return this.getFilteredItems();
	});

	public readonly activeFilters = signal<readonly ActiveFilter[]>([]);

	public readonly filterButtons: readonly IButton[] = [];

	public readonly filterSubtitle = computed<string | undefined>(() => {
		const totalItems = this.items().length;
		const filteredItems = this.filteredItems()?.length ?? 0;

		if (totalItems === filteredItems) {
			return `Showing all ${totalItems} items`;
		}

		return `Showing ${filteredItems} of ${totalItems} items`;
	});

	public readonly filters: readonly ExerciseFilter[] = [
		{
			applyFilter: (item, option) => {
				return item.equipment === option.value;
			},
			title: 'Equipment',
			options: ExerciseDb.equipment.map<SelectItemField<undefined>>((m) => {
				return { data: undefined, title: m, value: m };
			})
		},
		{
			applyFilter: (item, option) => {
				return item.difficulty === option.value;
			},
			title: 'Difficulty',
			options: ExerciseDb.difficultyLevels.map<SelectItemField<undefined>>((m) => {
				return { data: undefined, title: m, value: m };
			})
		},
		{
			applyFilter: (item, option) => {
				return item.targetMuscleGroup === option.value;
			},
			title: 'Muscle group',
			options: ExerciseDb.targetMuscleGroup.map<SelectItemField<undefined>>((m) => {
				return { data: undefined, title: m, value: m };
			})
		},
		{
			applyFilter: (item, option) => {
				return item.forceType === option.value;
			},
			title: 'Force type',
			options: ExerciseDb.forceType.map<SelectItemField<undefined>>((m) => {
				return { data: undefined, title: m, value: m };
			})
		},
		{
			applyFilter: (item, option) => {
				return item.laterality === option.value;
			},
			title: 'Laterality',
			options: ExerciseDb.laterality.map<SelectItemField<undefined>>((m) => {
				return { data: undefined, title: m, value: m };
			})
		}
	];

	public readonly filtersColumnsPerRow = computed<number>(() => {
		return this.isMobileView() ? 1 : 2;
	});

	public readonly displayedItems = computed<readonly ExtendedExerciseListItem[] | undefined>(() => {
		const pageSize = this.pageSize();
		let displayedItems = this.filteredItems();

		if (!displayedItems) {
			return undefined;
		}

		if (pageSize && pageSize > 0) {
			const takeItems = this.displayedItemsPage() * pageSize;
			displayedItems = displayedItems.slice(0, takeItems);
		}

		return displayedItems.map((m) => {
			const extendedItem: ExtendedExerciseListItem = {
				...m,
				subtitle: match(m)
					.returnType<string | undefined>()
					.with(
						{ equipment: P.nonNullable, targetMuscleGroup: P.nonNullable },
						(m) => `${m.equipment} - ${m.targetMuscleGroup}`
					)
					.with({ equipment: P.nonNullable }, (m) => m.equipment)
					.with({ targetMuscleGroup: P.nonNullable }, (m) => m.targetMuscleGroup)
					.otherwise(() => undefined),
				filteredImages: m.images,
				infoTableItems: [
					{ title: 'Equipment', value: m.equipment },
					{ title: 'Difficulty', value: m.difficulty },
					{ title: 'Muscle group', value: m.targetMuscleGroup },
					{ title: 'Force type', value: m.forceType },
					{ title: 'Laterality', value: m.laterality }
				]
			};

			return extendedItem;
		});
	});

	public readonly displayedItemsPage = signal<number>(1);

	public readonly showLoadMore = computed<boolean>(() => {
		const displayedItemsCount = this.displayedItems()?.length ?? 0;
		const filteredItemsCount = this.filteredItems()?.length ?? 0;

		if (displayedItemsCount < filteredItemsCount) {
			return true;
		}

		return false;
	});

	public readonly showLoadMoreCount = computed<number>(() => {
		const pageSize = this.pageSize();

		if (!pageSize) {
			return 0;
		}

		const displayedItemsCount = this.displayedItems()?.length ?? 0;
		const filteredItemsCount = this.filteredItems()?.length ?? 0;

		const remainingCount = filteredItemsCount - displayedItemsCount;

		if (remainingCount > pageSize) {
			return pageSize;
		}

		return remainingCount;
	});

	protected readonly showFilters = signal<boolean>(false);

	public readonly useSearchAndFilters = computed<boolean>(() => {
		const items = this.items();

		if (!items) {
			return false;
		}

		if (items.length > this.showSearchItemsThreshold) {
			return true;
		}
		return false;
	});

	constructor() {
		super();

		this.showFilters.set(this.getRememberShowFilters());

		effect(() => {
			this.rememberShowFilters(this.showFilters());
		});
	}

	setActiveFilter(filter: ExerciseFilter, option: SelectItemField<undefined>): void {
		this.activeFilters.update((value) => [
			...value.filter((m) => m.filter.title !== filter.title),
			{ filter, option }
		]);
	}

	handleClearFilter(filter: ExerciseFilter): void {
		this.activeFilters.update((value) => value.filter((m) => m.filter.title !== filter.title));
	}

	showImages(item: ExtendedExerciseListItem): boolean {
		if (item.filteredImages?.length) {
			return true;
		}
		return false;
	}

	handleSearch(search: ISearchInputResult): void {
		this.search.set(search);

		// reset page
		this.displayedItemsPage.set(1);
	}

	handleSelectItem(item: ExtendedExerciseListItem): void {
		item.select?.();
		this.selectItem.emit(item);
	}

	handleLoadMore(): void {
		this.displayedItemsPage.update((value) => value + 1);
	}

	private rememberShowFilters(showFilters: boolean): void {
		this.cookieService.setCookie('ShowExerciseFilters', showFilters ? 'true' : 'false');
	}

	private getRememberShowFilters(): boolean {
		return this.cookieService.getCookie('ShowExerciseFilters') === 'true';
	}

	private getFilteredItems(): readonly ExerciseListItem[] | undefined {
		const search = this.search();
		const items = this.items();
		const activeFilters = this.activeFilters();

		if (!items) {
			return undefined;
		}

		const filteredItems = match(activeFilters)
			.returnType<readonly ExerciseListItem[]>()
			.when(
				(m) => m.length,
				(m) => {
					return items.filter((item) => {
						return m.every((filter) => filter.filter.applyFilter(item, filter.option));
					});
				}
			)
			.otherwise(() => items);

		const searchFilteredItems = match(search)
			.returnType<readonly ExerciseListItem[]>()
			.with(P.nonNullable, (search) => {
				return filteredItems.filter((m) => {
					return stringHelper.containsAll(m.name, search.searchWords);
				});
			})
			.otherwise(() => filteredItems);

		if (search?.searchWords?.length) {
			return sortByRelevance({
				array: searchFilteredItems,
				search: search.search,
				orderByValue: (item) => item.name
			});
		}

		return this.sortByMedia(searchFilteredItems);
	}

	private sortByMedia(items: readonly ExerciseListItem[]): readonly ExerciseListItem[] {
		return items.toSorted((a, b) => {
			return this.getMediaWeight(b) - this.getMediaWeight(a);
		});
	}

	private getMediaWeight(item: ExerciseListItem): number {
		return (
			(item.images.length ? 1 : 0) +
			(item.videoUrl ? 3 : 0) +
			(item.equipment ? 1 : 0) +
			(item.instructions ? 1 : 0) +
			(item.targetMuscleGroup ? 1 : 0)
		);
	}
}
